import React, { useMemo, useState, useEffect } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import variables from '../../../../../stylesheets/variables.module.scss';
import { MemberState } from '../../../../../types/zinantis/members/membersType';
import MembersCard from './MemberCard';
import NavigationDots from './NavigationDots';
import NavigationArrows from './NavigationArrows';
import 'keen-slider/keen-slider.min.css';
import '../../../../../stylesheets/stylesComponents/members-carousel.scss';

interface MemberSegmentProps {
    segment: string;
    members: MemberState[];
    visibleSegmentName: boolean;
    visibleNewMember?: boolean;
    totalMembersCount: number;
}

const MemberSegment = (props: MemberSegmentProps) => {
    const {
        segment,
        members,
        visibleSegmentName,
        visibleNewMember = false,
        totalMembersCount,
    } = props;

    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        initial: 0,
        slides: { perView: 1 },
        breakpoints: {
            [`(min-width: ${variables.sm})`]: {
                slides: { perView: 2, spacing: 10 },
            },
            [`(min-width: ${variables.lg})`]: {
                slides: { perView: 3, spacing: 20 },
            },
            [`(min-width: ${variables.xl})`]: {
                slides: { perView: 4, spacing: 20 },
            },
        },
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    const [pageCount, setPageCount] = useState(0);
    const [maxIndex, setMaxIndex] = useState(0);

    useEffect(() => {
        if (instanceRef.current) {
            instanceRef.current.update();
            const maxIdx = instanceRef.current.track.details.maxIdx || 0;
            setMaxIndex(maxIdx);
            setPageCount(maxIdx + 1);
            setCurrentSlide(0);
            instanceRef.current.moveToIdx(0);
        }
    }, [members, instanceRef.current]);

    const membersCountText = useMemo(() => {
        if (members.length === totalMembersCount) return `(${members.length})`;
        return `(${members.length} / ${totalMembersCount})`;
    }, [members.length, totalMembersCount]);

    return (
        <div className='flex-column gap-10 padding-right10 carousel__wrapper'>
            <div className='flex-between flex-align-center'>
                <h5>
                    {segment}{' '}
                    <span style={{ fontWeight: 300, fontSize: '12px' }}>
                        {membersCountText}
                    </span>
                </h5>
                {loaded && instanceRef.current && (
                    <NavigationDots
                        totalDots={pageCount}
                        activeIndex={currentSlide}
                        onDotClick={index => {
                            instanceRef.current?.moveToIdx(index);
                        }}
                    />
                )}
            </div>
            <div ref={sliderRef} className='keen-slider'>
                {members.map(member => (
                    <MembersCard
                        key={member.id}
                        member={member}
                        visibleSegmentName={visibleSegmentName}
                        visibleNewMember={visibleNewMember}
                    />
                ))}
            </div>
            {loaded && instanceRef.current && (
                <NavigationArrows
                    onPrevClick={() => instanceRef.current?.prev()}
                    onNextClick={() => instanceRef.current?.next()}
                    isPrevActive={currentSlide > 0}
                    isNextActive={currentSlide < maxIndex}
                />
            )}
        </div>
    );
};

export default MemberSegment;
